import { useState, useEffect, useCallback } from "react";
import type { AdminDiscountCode, DiscountCodeLimits } from "../../lib/types";
import { adminListDiscountCodes, adminCreateDiscountCode, adminUpdateDiscountCode } from "../../lib/hooks";

// Same rule as the backend (voucher.Validate): capitals, digits, hyphens, 6–32 chars.
const CODE_PATTERN = /^[A-Z0-9-]{6,32}$/;

const API_ERRORS: Record<string, string> = {
  "Discount code already exists": "Tento kód už existuje",
  "A gift voucher already uses this code": "Tento kód už používa darčekový kupón",
};

function formatEuro(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",") + " €";
}

/** "−10 %" or "−10,00 €". */
function discountValue(dc: { percent?: number; amount_cents?: number }): string {
  return dc.amount_cents ? `−${formatEuro(dc.amount_cents)}` : `−${dc.percent} %`;
}

function formatDate(iso: string): string {
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

/** Why a code can't be used right now, for the badge; "Aktívny" when it can. */
function statusOf(dc: AdminDiscountCode): { label: string; className: string } {
  if (dc.usable) return { label: "Aktívny", className: "status-active" };
  if (!dc.is_active) return { label: "Vypnutý", className: "status-void" };
  if (dc.max_uses !== null && dc.uses >= dc.max_uses) return { label: "Vyčerpaný", className: "status-redeemed" };
  return { label: "Expirovaný", className: "status-redeemed" };
}

/** "" → unlimited; otherwise a whole number ≥ 1, or undefined when invalid. */
function parseMaxUses(raw: string): number | null | undefined {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : undefined;
}

/** The typed value as the API wants it, or null when it isn't valid. */
function parseDiscountValue(kind: "percent" | "amount", raw: string): { percent: number } | { amount_cents: number } | null {
  if (kind === "percent") {
    const pct = Number(raw);
    return Number.isInteger(pct) && pct >= 1 && pct <= 100 ? { percent: pct } : null;
  }
  const euros = parseFloat(raw.replace(",", "."));
  return !isNaN(euros) && euros > 0 ? { amount_cents: Math.round(euros * 100) } : null;
}

function limitsOf(dc: AdminDiscountCode): DiscountCodeLimits {
  return { expires_at: dc.expires_at ?? "", max_uses: dc.max_uses, is_active: dc.is_active, note: dc.note ?? "" };
}

export function ManageDiscountCodes() {
  const [codes, setCodes] = useState<AdminDiscountCode[]>([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<"percent" | "amount">("percent");
  const [value, setValue] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [created, setCreated] = useState<AdminDiscountCode | null>(null);

  // Row edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editExpiresAt, setEditExpiresAt] = useState("");
  const [editMaxUses, setEditMaxUses] = useState("");
  const [editNote, setEditNote] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowError, setRowError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await adminListDiscountCodes();
    setCodes(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const codeInvalid = code !== "" && !CODE_PATTERN.test(code);

  const handleCreate = async () => {
    if (!CODE_PATTERN.test(code)) {
      setFormError("Kód môže obsahovať len veľké písmená A–Z, číslice a pomlčky, 6 až 32 znakov");
      return;
    }
    const parsed = parseDiscountValue(kind, value);
    if (!parsed) {
      setFormError(kind === "percent" ? "Zľava musí byť celé číslo od 1 do 100 %" : "Zadajte platnú sumu v €");
      return;
    }
    const limit = parseMaxUses(maxUses);
    if (limit === undefined) {
      setFormError("Limit použití musí byť celé číslo aspoň 1, alebo prázdne");
      return;
    }
    setSaving(true);
    setFormError("");
    setCreated(null);
    const { data, error } = await adminCreateDiscountCode({ code, ...parsed, expires_at: expiresAt, max_uses: limit, note: note.trim() });
    setSaving(false);
    if (error || !data) {
      setFormError(API_ERRORS[error?.message ?? ""] ?? `Chyba: ${error?.message}`);
      return;
    }
    setCreated(data);
    setCode("");
    setValue("");
    setExpiresAt("");
    setMaxUses("");
    setNote("");
    load();
  };

  const update = async (dc: AdminDiscountCode, limits: DiscountCodeLimits) => {
    setBusyId(dc.id);
    setRowError("");
    const { error } = await adminUpdateDiscountCode(dc.id, limits);
    setBusyId(null);
    if (error) {
      setRowError(`Chyba: ${error.message}`);
      return false;
    }
    load();
    return true;
  };

  const startEdit = (dc: AdminDiscountCode) => {
    setEditingId(dc.id);
    setEditExpiresAt(dc.expires_at ?? "");
    setEditMaxUses(dc.max_uses === null ? "" : String(dc.max_uses));
    setEditNote(dc.note ?? "");
    setRowError("");
  };

  const saveEdit = async (dc: AdminDiscountCode) => {
    const limit = parseMaxUses(editMaxUses);
    if (limit === undefined) {
      setRowError("Limit použití musí byť celé číslo aspoň 1, alebo prázdne");
      return;
    }
    const ok = await update(dc, { ...limitsOf(dc), expires_at: editExpiresAt, max_uses: limit, note: editNote.trim() });
    if (ok) setEditingId(null);
  };

  const renderRow = (dc: AdminDiscountCode) => {
    const status = statusOf(dc);
    const busy = busyId === dc.id;
    return (
      <div key={dc.id} className={`admin-voucher-item ${dc.is_active ? "" : "status-void"}`}>
        <div className="admin-voucher-main">
          <span className="admin-voucher-code">{dc.code}</span>
          <span className="admin-voucher-value">{discountValue(dc)}</span>
          <span className={`admin-voucher-status ${status.className}`}>{status.label}</span>
          <span className="admin-voucher-source">
            Použitý {dc.uses}×{dc.max_uses !== null ? ` z ${dc.max_uses}` : ""}
          </span>
        </div>
        {editingId === dc.id ? (
          <div className="form-row admin-discount-edit">
            <div className="form-field">
              <label>Platí do:</label>
              <input type="date" value={editExpiresAt} onChange={(e) => setEditExpiresAt(e.target.value)} />
            </div>
            <div className="form-field">
              <label>Limit použití:</label>
              <input type="number" min="1" step="1" value={editMaxUses} onChange={(e) => setEditMaxUses(e.target.value)} placeholder="bez limitu" />
            </div>
            <div className="form-field">
              <label>Poznámka:</label>
              <input type="text" value={editNote} onChange={(e) => setEditNote(e.target.value)} />
            </div>
          </div>
        ) : (
          <div className="admin-voucher-meta">
            <span>Vytvorený {formatDate(dc.created_at)}</span>
            <span>{dc.expires_at ? `Platí do ${formatDate(dc.expires_at)}` : "Bez expirácie"}</span>
            {dc.note && <span>{dc.note}</span>}
          </div>
        )}
        <div className="admin-voucher-actions">
          {editingId === dc.id ? (
            <>
              <button className="btn btn-primary btn-small" onClick={() => saveEdit(dc)} disabled={busy}>
                {busy ? "Ukladám..." : "Uložiť"}
              </button>
              <button className="btn btn-secondary btn-small" onClick={() => setEditingId(null)} disabled={busy}>
                Zrušiť
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary btn-small" onClick={() => startEdit(dc)} disabled={busy}>
                Upraviť
              </button>
              <button
                className="btn btn-secondary btn-small"
                onClick={() => update(dc, { ...limitsOf(dc), is_active: !dc.is_active })}
                disabled={busy}
              >
                {dc.is_active ? "Vypnúť" : "Zapnúť"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-vouchers">
      <h2>Zľavové kódy</h2>

      <div className="admin-card">
        <h3>Nový zľavový kód</h3>
        <div className="form-row">
          <div className="form-field">
            <label>Kód:</label>
            <input
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setFormError(""); }}
              placeholder="napr. KAVIAREN-BB"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={32}
              className={codeInvalid ? "input-invalid" : ""}
            />
            <span className="form-hint">Len veľké písmená A–Z, číslice a pomlčky, 6–32 znakov.</span>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Typ zľavy:</label>
            <div className="resource-picker">
              <button type="button" className={`resource-pick-btn ${kind === "percent" ? "active" : ""}`} onClick={() => { setKind("percent"); setValue(""); setFormError(""); }}>
                Percentá
              </button>
              <button type="button" className={`resource-pick-btn ${kind === "amount" ? "active" : ""}`} onClick={() => { setKind("amount"); setValue(""); setFormError(""); }}>
                Suma v €
              </button>
            </div>
            <span className="form-hint">
              {kind === "percent"
                ? "Zľava z každej hodiny. Zákazník ho môže použiť opakovane."
                : "Odpočíta sa z prvej hodiny rezervácie, najviac do jej ceny (napr. 10 € = skúšobná hodina zadarmo). Každý zákazník ho môže použiť len raz."}
            </span>
          </div>
          <div className="form-field">
            <label>{kind === "percent" ? "Zľava (%):" : "Zľava (€):"}</label>
            {kind === "percent" ? (
              <input type="number" min="1" max="100" step="1" value={value} onChange={(e) => setValue(e.target.value)} placeholder="napr. 10" />
            ) : (
              <input type="number" min="0.01" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} placeholder="napr. 10" />
            )}
            <span className="form-hint">Neskôr sa nedá zmeniť. Iná zľava = nový kód.</span>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Platí do (voliteľné):</label>
            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            <span className="form-hint">Vrátane toho dňa. Prázdne = bez expirácie, kým kód nevypnete.</span>
          </div>
          <div className="form-field">
            <label>Limit použití (voliteľný):</label>
            <input type="number" min="1" step="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="bez limitu" />
            <span className="form-hint">Počet rezervácií s kódom. Zrušené sa nerátajú.</span>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Poznámka (voliteľná):</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="napr. výmena so susednou kaviarňou" />
          </div>
        </div>
        {formError && <div className="admin-result warning">{formError}</div>}
        {created && (
          <div className="admin-result success">
            Kód vytvorený: <strong className="admin-voucher-code">{created.code}</strong> {discountValue(created)}
            {created.expires_at ? `, platí do ${formatDate(created.expires_at)}` : ""}.
          </div>
        )}
        <button className="btn btn-primary" onClick={handleCreate} disabled={saving || !code || !value}>
          {saving ? "Ukladám..." : "Vytvoriť kód"}
        </button>
      </div>

      {rowError && <div className="admin-result warning">{rowError}</div>}
      {loading ? (
        <div className="loading-small">Načítavam...</div>
      ) : codes.length === 0 ? (
        <p className="empty">Žiadne zľavové kódy.</p>
      ) : (
        <div className="admin-voucher-list">{codes.map(renderRow)}</div>
      )}
    </div>
  );
}
